import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Nav } from './components/Nav'
import { Footer } from './components/Footer'
import { WhatsAppBubble } from './components/WhatsAppBubble'
import { usePageView } from './hooks/usePageView'
import { useScrollToTop } from './hooks/useScrollToTop'
import { languageFromPath, htmlLangFor, loadLanguage } from './i18n/i18n'
import Landing from './routes/Landing'
import Destinations from './routes/Destinations'
import DestinationDetail from './routes/DestinationDetail'
import Account from './routes/Account'
import Login from './routes/Login'
import Signup from './routes/Signup'
import AuthCallback from './routes/AuthCallback'
import Faq from './routes/Faq'
import About from './routes/About'
import HowItWorks from './routes/HowItWorks'
import CompatibleDevices from './routes/CompatibleDevices'
import RefundPolicy from './routes/RefundPolicy'
import Privacy from './routes/Privacy'
import { importCheckout } from './lib/checkoutWarmup'

// Checkout and OrderConfirmation are split out of the entry bundle. The split
// first existed because importing @stripe/stripe-js injects
// js.stripe.com/stripe.js (244 KB) as a top-level side effect, which put
// Stripe in every page's first-paint window, the Google Ads landing page
// included. Nothing imports that entry any more: stripe.js loads only through
// getStripe() in lib/stripe.ts (the side-effect-free '@stripe/stripe-js/pure'
// entry). That happens on checkout mount, where Stripe wants it for Radar's
// fraud signals; on the confirmation page when it checks a payment; and on
// the plan pages once they are idle or a plan is hovered
// (lib/checkoutWarmup.ts). The two routes stay lazy because they are still
// big.
//
// The Checkout import() lives in lib/checkoutWarmup.ts so that the plan pages'
// prefetch and this lazy() share one chunk.
const Checkout = lazy(importCheckout)
const OrderConfirmation = lazy(() => import('./routes/OrderConfirmation'))

// CRM is admin-only and big — code-split into its own chunk so the customer
// bundle doesn't pay for it.
const AdminLayout = lazy(() => import('./admin/AdminLayout'))
const CrmDashboard = lazy(() => import('./admin/pages/Dashboard'))
const CrmOrders = lazy(() => import('./admin/pages/Orders'))
const CrmCustomers = lazy(() => import('./admin/pages/Customers'))
const CrmCoupons = lazy(() => import('./admin/pages/Coupons'))
const CrmCatalog = lazy(() => import('./admin/pages/Catalog'))
const CrmAnalytics = lazy(() => import('./admin/pages/Analytics'))
const CrmAcquisition = lazy(() => import('./admin/pages/Acquisition'))
const CrmAffiliates = lazy(() => import('./admin/pages/Affiliates'))

// The affiliate partner portal is a separate audience (travel agencies, not
// travellers) with its own layout and CSS — same reasoning as CRM: split it
// out so the customer bundle never carries it.
const PartnerLayout = lazy(() => import('./partner/PartnerLayout'))
const PartnerLogin = lazy(() => import('./partner/pages/Login'))
const PartnerOverview = lazy(() => import('./partner/pages/Overview'))
const PartnerOrders = lazy(() => import('./partner/pages/Orders'))
const PartnerPayouts = lazy(() => import('./partner/pages/Payouts'))
const PartnerGroups = lazy(() => import('./partner/pages/Groups'))
const PartnerGroupDetail = lazy(() => import('./partner/pages/GroupDetail'))
const PartnerBills = lazy(() => import('./partner/pages/Bills'))
const GroupClaim = lazy(() => import('./routes/GroupClaim'))
const PartnerTerms = lazy(() => import('./partner/pages/Terms'))

function Shell() {
  return (
    <>
      <Nav />
      <main style={{ flex: 1 }}>
        {/* Boundary for the lazy Checkout / OrderConfirmation chunks. It sits
            inside <main> rather than around <Routes> so the nav and footer
            stay put while a chunk loads. The min-height stops the footer
            jumping up into the gap, which would otherwise register as layout
            shift on the way into checkout. */}
        {/* 60vh was tuned for in-app navigation to checkout, where the
            surrounding page is already painted. Now that every route is a
            chunk, this fallback is also what the FIRST paint of a cold load
            renders — and at 60vh the footer painted mid-screen then jumped
            down when the route arrived, measuring 0.316 CLS. Reserving a
            full viewport keeps the footer below the fold until real content
            replaces it. */}
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <WhatsAppBubble />
    </>
  )
}

// Sync i18next.language with the URL path (/zh/* → zh, /es/* → es,
// otherwise en) and keep <html lang> in sync for SEO + a11y. Sits at
// the top of the route tree so every page reads the right language on
// first render.
// Route trees that own their own language and must NOT be driven from the URL.
// The partner portal has no locale-prefixed twin — agencies arrive on a bare
// /partner link — so it stores the choice per browser instead.
const SELF_MANAGED_LANG_PREFIXES = ['/partner', '/g']

function LanguageSync() {
  const location = useLocation()
  const { i18n } = useTranslation()
  const lang = languageFromPath(location.pathname)
  // Bail on self-managed trees. languageFromPath reads /partner/* as English,
  // so without this the portal's own choice and this effect overwrite each
  // other — and because each changeLanguage re-renders the subscribers that
  // trigger the other side, the two spin the main thread until Chrome offers
  // to kill the tab. Reported as "the whole browser freezes" on /partner/login
  // by anyone who had picked 中文.
  const selfManaged = SELF_MANAGED_LANG_PREFIXES.some((p) =>
    location.pathname === p || location.pathname.startsWith(p + '/'),
  )
  useEffect(() => {
    if (selfManaged) return
    // Fetch the language chunk before switching: changeLanguage on a
    // not-yet-loaded language would render raw keys for a frame.
    //
    // The cancelled flag matters because i18n.language is not updated until
    // the fetch resolves, so this effect cannot use it to detect that the
    // user has moved on. Without it, switching to Chinese on a slow link and
    // pressing Back repaints the English page in Chinese, and nothing ever
    // corrects it.
    let cancelled = false
    if (i18n.language !== lang) {
      void loadLanguage(lang).finally(() => {
        if (!cancelled) void i18n.changeLanguage(lang)
      })
    }
    document.documentElement.lang = htmlLangFor(lang)
    return () => {
      cancelled = true
    }
  }, [lang, i18n, selfManaged])
  return null
}

export default function App() {
  usePageView()
  useScrollToTop()
  return (
    <>
      <LanguageSync />
      <Routes>
        {/* English (default — no path prefix) */}
        <Route element={<Shell />}>
          <Route path="/" element={<Landing />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/destinations/:country" element={<DestinationDetail />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:id" element={<OrderConfirmation />} />
          <Route path="/account" element={<Account />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/compatible-devices" element={<CompatibleDevices />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy" element={<Privacy />} />
        </Route>

        {/* Simplified Chinese (under /zh prefix). Routes mirror English
            exactly — keep them in sync when adding new pages. */}
        <Route path="/zh" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* Latin American Spanish (under /es prefix). Same shape as /zh. */}
        <Route path="/es" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* French (under /fr prefix). */}
        <Route path="/fr" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* Japanese (under /ja prefix). */}
        <Route path="/ja" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* Korean (under /ko prefix). */}
        <Route path="/ko" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* German (under /de prefix). */}
        <Route path="/de" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* Italian (under /it prefix). */}
        <Route path="/it" element={<Shell />}>
          <Route index element={<Landing />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinations/:country" element={<DestinationDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order/:id" element={<OrderConfirmation />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="compatible-devices" element={<CompatibleDevices />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />

      {/* Admin/CRM lives outside the Shell wrapper — its own full-bleed layout. */}
      <Route
        path="/admin"
        element={
          <Suspense fallback={<div style={{ padding: 64, fontFamily: 'system-ui' }}>Loading admin…</div>}>
            <AdminLayout />
          </Suspense>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<CrmDashboard />} />
        <Route path="orders" element={<CrmOrders />} />
        <Route path="orders/:reference" element={<CrmOrders />} />
        <Route path="customers" element={<CrmCustomers />} />
        <Route path="coupons" element={<CrmCoupons />} />
        <Route path="catalog" element={<CrmCatalog />} />
        <Route path="analytics" element={<CrmAnalytics />} />
        <Route path="acquisition" element={<CrmAcquisition />} />
        <Route path="affiliates" element={<CrmAffiliates />} />
      </Route>

      {/* Partner portal — like /admin, deliberately NOT locale-prefixed;
          languageFromPath resolves /partner/* to 'en' and the shell has its
          own in-portal language switch. */}
      {/* Sign-in sits OUTSIDE the layout route: PartnerLayout is gated, so a
          logged-out partner would never reach a login page nested inside it. */}
            <Route path="/g/:code" element={<GroupClaim />} />
      <Route
        path="/partner/login"
        element={
          <Suspense fallback={<div style={{ padding: 64, fontFamily: 'system-ui' }}>Loading…</div>}>
            <PartnerLogin />
          </Suspense>
        }
      />
      <Route
        path="/partner"
        element={
          <Suspense fallback={<div style={{ padding: 64, fontFamily: 'system-ui' }}>Loading portal…</div>}>
            <PartnerLayout />
          </Suspense>
        }
      >
        {/* A sign-in link returns only to the pages listed in the backend's
            routers/affiliate.py _PORTAL_PAGE: add a new page there too. */}
        <Route index element={<Navigate to="/partner/overview" replace />} />
        <Route path="overview" element={<PartnerOverview />} />
        <Route path="orders" element={<PartnerOrders />} />
        <Route path="payouts" element={<PartnerPayouts />} />
        <Route path="groups" element={<PartnerGroups />} />
        <Route path="groups/:groupId" element={<PartnerGroupDetail />} />
        <Route path="bills" element={<PartnerBills />} />
        <Route path="terms" element={<PartnerTerms />} />
      </Route>
    </Routes>
    </>
  )
}
